import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';

/**
 * Shared list + search + paginate hook for the Settings hub tabs.
 *
 * Each tab supplies the entity's list endpoint + payload shape; this hook
 * fetches once on mount (and on the supplied `dependencies` change), filters
 * client-side by the active `q` against the row fields the caller declares
 * in `searchFields`, and slices to `pageSize` rows per page.
 *
 * The same hook also exposes `removeRow` so a parent action (disable / delete)
 * can mutate the cached list without a refetch.
 */
export function useEntityCrud({
    endpoint,                 // e.g. `${config.hostname}/country/getcountrybyconcessionaire`
    payload,                  // POST body, e.g. { country_concessionaire_id }
    searchFields = [],        // e.g. ['country_name', 'country_code']
    q = '',
    page = 1,
    pageSize = 15,
    dependencies = [],
}) {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const token = typeof window !== 'undefined' ? localStorage.getItem('sessiontoken') : '';
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        axios
            .post(endpoint, payload, { headers })
            .then((res) => {
                if (cancelled) return;
                const data = Array.isArray(res.data) ? res.data : [];
                setRows(data);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('useEntityCrud:', err);
                setError(err);
                toast.error(t('Failed to load list'));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [endpoint, JSON.stringify(payload), reloadKey, ...dependencies]);

    // Client-side search across the configured fields. Nested paths are
    // supported via dotted strings, e.g. 'category_id.category_name'.
    const filtered = useMemo(() => {
        const needle = String(q || '').trim().toLowerCase();
        if (!needle) return rows;
        return rows.filter((row) =>
            searchFields.some((path) => {
                const value = path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), row);
                return String(value || '').toLowerCase().includes(needle);
            }),
        );
    }, [rows, q, searchFields]);

    const total = filtered.length;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(Math.max(1, page), pageCount);
    const paged = useMemo(
        () => filtered.slice((safePage - 1) * pageSize, safePage * pageSize),
        [filtered, safePage, pageSize],
    );

    const removeRow = useCallback((id) => {
        setRows((prev) => prev.filter((r) => r._id !== id));
    }, []);

    const updateRow = useCallback((id, patch) => {
        setRows((prev) => prev.map((r) => (r._id === id ? { ...r, ...patch } : r)));
    }, []);

    return { rows, filtered, paged, total, pageCount, page: safePage, loading, error, refresh, removeRow, updateRow };
}

export default useEntityCrud;
